import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useRoutes } from 'react-router-dom';
import { Method } from './fetch/core/util';
import zhCN from 'antd/lib/locale/zh_CN';
import {
  GET_ENUM_LIST_MAP,
  GetCockpitStatus,
  MQTT_TOKEN,
  USER_INFO,
} from './redux/actions/common';
import { sendHttpRequest } from './redux/fetchHandler';
import { RootState } from './redux/store';
import routes from './routes';
import { HttpStatusCode } from './fetch/core/constant';
import { isEmpty, wsSendMessage } from './utils/util';

import { EventName, MessageType } from './utils/constant';
import { ConfigProvider, message } from 'antd';
import { sendGlobalEvent } from './utils/emit';
import { CockpitStatus, CockpitType } from './utils/type';
import { actionRestIsInit } from './redux/reducers/newCommon';
import '@/assets/css/all.scss';
import '@jd/x-coreui/lib/index.css';
import { CommonEnumKey } from './utils/enum';
import { driveDataDTOType } from '@/utils/protoTypes';

const App = () => {
  const routing = useRoutes(routes);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const connectStatusInterval = useRef<any>(null);
  const passportTimer = useRef<any>(null);
  const { login, enumTranslator, newCommon } = useSelector(
    (state: RootState) => state
  );
  const handlePassportValidate = () => {
    const loginParams: HTTPFetchActionParams = {
      absoluteURL: `${location.protocol}//${process.env.JDX_APP_FETCH_DATA_DOMAIN}/authentication/server/login/passport_login`,
      method: 'GET',
      urlParams: {
        appCode: 'parallel_driving',
      },
      actionType: 'GET_PASSPORT_TOKEN',
      nextActionFunc: (resp: any) => {
        if (
          resp.code !== HttpStatusCode.Success &&
          localStorage.getItem('logout') !== 'logout'
        ) {
          return;
        }
        localStorage.setItem('reload', 'false');
        localStorage.removeItem('logout');
      },
    };
    dispatch(sendHttpRequest(loginParams));
  };
  const getConfInfo = () => {
    const wsMessage = driveDataDTOType.create({
      requestHeader: {
        needResponse: true,
        retry: true,
        requestId: Date.now() * 1000,
        clientName: 'screen',
        requestTime: Date.now() * 1000,
      },
      messageType: [MessageType.CONF_INFO],
    });
    const buffer = driveDataDTOType.encode(wsMessage).finish();

    wsSendMessage(buffer);
  };

  const keepAlive = () => {
    connectStatusInterval.current = setInterval(() => {
      const wsMessage = driveDataDTOType.create({
        requestHeader: {
          needResponse: false,
          retry: true,
          requestId: Date.now() * 1000,
          clientName: 'screen',
          requestTime: Date.now() * 1000,
        },
        messageType: [MessageType.CONNECT_STATUS],
      });
      const buffer = driveDataDTOType.encode(wsMessage).finish();
      wsSendMessage(buffer);
    }, 3000);
  };

  const getCurrentUserInfoData = (cb?: any) => {
    if (login?.userInfo?.userName) {
      return;
    }
    const loginData: HTTPFetchActionParams = {
      method: 'POST',
      path: '/authentication/getLoginUserInfo',
      body: {
        appCode: 'parallel_driving',
      },
      actionType: USER_INFO,
      nextActionFunc: (resp: any) => {
        if (resp?.code === HttpStatusCode.Success) {
          cb && cb(resp);
        } else {
          message.warning(resp?.message);
        }
      },
    };
    dispatch(sendHttpRequest(loginData));
  };

  const getMqttLoginToken = () => {
    const requestData: HTTPFetchActionParams = {
      path: '/authentication/getMqttLoginToken',
      method: Method.POST,
      actionType: MQTT_TOKEN,
    };

    dispatch(sendHttpRequest(requestData));
  };

  const getCockpitStatus = () => {
    const requestData: HTTPFetchActionParams = {
      path: '/monitor/web/user/getCockpitStatus',
      method: Method.POST,
      actionType: GetCockpitStatus,
      nextActionFunc: (res) => {
        if (res.code === HttpStatusCode.Success) {
          const { cockpitType, cockpitNumber, cockpitMode, cockpitStatus } =
            res?.data || {};
          const isLoginOrHomePage =
            location.pathname.includes('/login') ||
            location.pathname.includes('/home');
          if (
            !isLoginOrHomePage &&
            (cockpitStatus === CockpitStatus.OFFLINE ||
              !cockpitStatus ||
              cockpitType === CockpitType.MONITOR_SEAT ||
              !cockpitType)
          ) {
            location.href = '/app/home';
            return;
          }
          if (!cockpitMode || !cockpitStatus) {
            return;
          }
          if (
            cockpitType === CockpitType.MONITOR_SEAT ||
            cockpitNumber !== newCommon.cockpitNumber
          ) {
            message.error(
              `您已入座${cockpitNumber}座席，请退出座席后再重新入座`
            );
            return;
          } else {
            setTimeout(() => {
              sendGlobalEvent(EventName.CREATE_MQTT_CLIENT);
              if (
                !location.pathname.includes('multivehicle') &&
                !location.pathname.includes('singleview')
              ) {
                navigate('/app/multivehicle');
              }
            }, 600);
          }
        }
        dispatch(actionRestIsInit()); // 取消页面初始化标识
      },
    };
    dispatch(sendHttpRequest(requestData));
  };

  const getEnumTranslator = () => {
    if (enumTranslator.alarmEventList) {
      return;
    }
    const allEnumValues = Object.values(CommonEnumKey) || [];
    const requestData: HTTPFetchActionParams = {
      path: '/monitor/web/enum/get_enum_list_map',
      method: 'POST',
      body: {
        keyList: allEnumValues,
      },
      actionType: GET_ENUM_LIST_MAP,
    };
    dispatch(sendHttpRequest(requestData));
  };

  useEffect(() => {
    // 在登录页面刷新时调用passport
    if (login.userInfo) {
      return;
    }
    if (
      location.pathname.includes('login') ||
      localStorage.getItem('reload') === 'true'
    ) {
      passportTimer.current = setTimeout(() => {
        clearTimeout(passportTimer.current);
        handlePassportValidate();
      }, 500);
    } else {
      getCurrentUserInfoData();
    }
    return () => {
      clearTimeout(passportTimer.current);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname.includes('/app/singleview')) {
      handlePassportValidate();
    }
  }, [location.pathname]);
  useEffect(() => {
    if (login.token) {
      setTimeout(() => {
        getCurrentUserInfoData((res) => {
          if (res.code === HttpStatusCode.Success) {
            if (isEmpty(res.data?.resourceList)) {
              message.warning('您当前无访问权限，请联系管理员添加权限。');
              return;
            }
            navigate('/app/home');
          } else {
            message.warning(res?.message);
          }
        });
      }, 1000);
    }
  }, [login.token]);
  useEffect(() => {
    const interval = setInterval(() => {
      if ((window as any).wsClient) {
        clearInterval(interval);
        getConfInfo();
        keepAlive();
      }
      return () => {
        clearInterval(interval);
        clearInterval(connectStatusInterval.current);
      };
    }, 50);
  }, []);
  useEffect(() => {
    if (login?.userInfo?.userName) {
      getMqttLoginToken();
      getEnumTranslator();
    }
  }, [login?.userInfo?.userName]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (newCommon.cockpitNumber && login?.userInfo?.userName) {
        clearInterval(timer);
        getCockpitStatus();
      }
    }, 50);
    return () => {
      clearInterval(timer);
    };
  }, [newCommon.cockpitNumber, login?.userInfo?.userName]);

  useEffect(() => {
    if (
      (!newCommon.cockpitMode ||
        !newCommon.cockpitStatus ||
        newCommon.cockpitStatus === CockpitStatus.OFFLINE) &&
      !location.pathname.includes('login') &&
      !location.pathname.includes('home') &&
      !location.pathname.includes('singleview') &&
      !newCommon.isInit && // 刷新页面算初始化
      !(window as any).globalNotShowOfflineTip
    ) {
      message.error('您已断线，请重新入座座席');
      navigate('/app/home');
    }
  }, [
    newCommon.cockpitMode,
    newCommon.cockpitStatus,
    location.pathname,
    newCommon.isInit,
  ]);
  return (
    <ConfigProvider locale={zhCN}>
      <div className="main-container">{routing}</div>
    </ConfigProvider>
  );
};

export default React.memo(App);
